import { OutlinedButton } from '@/components/layouts/Buttons/OutlinedButton/OutlinedButton';
import { Bold, Card, Text, Title } from '@tremor/react';
import type { ReactNode } from 'react';
import { ClipLoader } from 'react-spinners';
import type { InvitationLoaderView } from './invitationLoaderView';

type Props = {
  readonly view: InvitationLoaderView;
  readonly isAccepting: boolean;
  readonly acceptError: string | null;
  readonly onLogInAsAnotherUser: () => void;
  readonly onGoHome: () => void;
};

export function InvitationCardContent({
  view,
  isAccepting,
  acceptError,
  onLogInAsAnotherUser,
  onGoHome,
}: Props) {
  const title = getInvitationTitle(view, acceptError);
  const body = invitationViewToBody(view, acceptError);
  const isWrongUserError = view.kind === 'error' && view.isWrongUser;
  const showInvitationPreview = view.kind === 'workspace' || view.kind === 'organization';
  const showLoaderErrorActions = view.kind === 'error' && !view.isWrongUser;
  const showAcceptFailure = Boolean(acceptError);
  const showAcceptingSpinner = showInvitationPreview && isAccepting && !acceptError;

  return (
    <Card className="w-full p-6">
      <Title className="mb-4 text-2xl">{title}</Title>
      {body}
      {isWrongUserError ? (
        <OutlinedButton onClick={onLogInAsAnotherUser}>
          Log in as another user
        </OutlinedButton>
      ) : null}
      {showLoaderErrorActions ? (
        <OutlinedButton onClick={onGoHome}>Go to home</OutlinedButton>
      ) : null}
      {showAcceptingSpinner ? (
        <div
          className="flex flex-col items-center justify-center gap-4 py-6"
          role="status"
          aria-live="polite"
        >
          <ClipLoader
            size={56}
            color="#BA2634"
            speedMultiplier={0.9}
            cssOverride={{ borderWidth: '4px' }}
          />
          <Text className="text-base">Accepting your invitation…</Text>
        </div>
      ) : null}
      {showAcceptFailure ? (
        <>
          <Text className="mb-6 text-base text-red-600">{acceptError}</Text>
          <OutlinedButton onClick={onGoHome}>Go to home</OutlinedButton>
        </>
      ) : null}
    </Card>
  );
}

function getInvitationTitle(
  view: InvitationLoaderView,
  acceptError: string | null,
): string {
  if (acceptError && (view.kind === 'workspace' || view.kind === 'organization')) {
    return "We couldn't complete your invitation";
  }
  if (view.kind === 'error') {
    if (view.isWrongUser) {
      return 'Wrong account for this invitation';
    }
    return "This invitation link isn't valid";
  }
  if (view.kind === 'unknown') {
    return "We couldn't load this invitation";
  }
  return 'You have a pending invitation!';
}

function invitationViewToBody(
  view: InvitationLoaderView,
  acceptError: string | null,
): ReactNode {
  if (acceptError && (view.kind === 'workspace' || view.kind === 'organization')) {
    return (
      <Text className="mb-6 text-base text-gray-600">
        The invitation may have expired, already been used, or the link may no longer be
        active. Ask your admin for a new invitation if you still need access.
      </Text>
    );
  }
  if (view.kind === 'workspace') {
    return (
      <Text className="mb-8 text-base">
        You have been invited to join workspace <Bold>{view.workspaceName}</Bold> in
        organization <Bold>{view.organizationName}</Bold>
      </Text>
    );
  }
  if (view.kind === 'organization') {
    return (
      <Text className="mb-8 text-base">
        You have been invited to join organization <Bold>{view.organizationName}</Bold>
      </Text>
    );
  }
  if (view.kind === 'error') {
    return <Text className="mb-8 text-base">{view.message}</Text>;
  }
  return (
    <Text className="mb-8 text-base text-gray-600">
      This link may be incomplete or the invitation data is missing. Check that you opened
      the full link from your email, or request a new invitation.
    </Text>
  );
}
